import React, { useState, useRef, useEffect } from 'react';
import { TextField, Typography, InputAdornment } from '@mui/material';
import StyledTextField from '../styled-components/StyledTextField';
import { MAX_LENGTH_FIELDS } from '../../constants/field-constants';
import AlertService from '../../services/notification.service';
import { NON_DiGIT_PATTERN, DIGITS_ONLY_PATTERN } from '../../constants/patterns-constants';

type TextFieldProps = React.ComponentProps<typeof TextField>;

interface NumericTextFieldProps extends TextFieldProps {
  errorFields?: boolean;
  error?: boolean;
  setErrorFields?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  name: string;
  maxLength?: number;
  disabled?: boolean;
  required?: boolean;
  check?: boolean;
  minValue?: number;
  maxValue?: number;
}

export const NumericTextField = ({
                                    value,
                                    onChange,
                                    label,
                                    name,
                                    disabled,
                                    required,
                                    check,
                                    errorFields,
                                    error,
                                    setErrorFields,
                                    maxLength,
                                    minValue,
                                    maxValue,
                                    ...rest
                                  } :NumericTextFieldProps) => {

  const wasAlertedRef = useRef(false);
  const wasFocusedRef = useRef(false);
  const wasTrimmedRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (isFocused) {
      setErrorFields?.(prev => ({...prev, [name]: false}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isFocused]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let rawValue: string = e.target.value;
    let trimmed: boolean = false;
    if (rawValue.length > (maxLength ? maxLength : MAX_LENGTH_FIELDS)) {
      rawValue = rawValue.slice(0, maxLength ? maxLength : MAX_LENGTH_FIELDS);
      trimmed = true;
    }
    const cleaned = rawValue.replace(NON_DiGIT_PATTERN, '');
    const synthetic = { target: { value: cleaned } } as React.ChangeEvent<HTMLInputElement>;

    if (trimmed && !wasTrimmedRef.current) {
      AlertService.warning(`The text entered in the ${label} field was truncated to ${maxLength ? maxLength : MAX_LENGTH_FIELDS} characters`);
      wasTrimmedRef.current = true;
      setErrorFields?.(prev => ({...prev, [name]: true}));
    } else {
      setErrorFields?.(prev => ({...prev, [name]: false}));
    }

    if (cleaned !== rawValue && !wasAlertedRef.current) {
      AlertService.warning(`Invalid characters have been removed from the ${label} field. Only numbers are allowed.`);
      wasAlertedRef.current = true;
      setErrorFields?.(prev => ({...prev, [name]: true}));
    } else {
      setErrorFields?.(prev => ({...prev, [name]: false}));
    }
     onChange?.(synthetic);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const stringValue = String(value ?? '').trim();
    if (required && !stringValue && check)  {
       AlertService.warning(`Please fill in the field ${label}.`);
       setErrorFields?.(prev => ({...prev, [name]: true}));
       return;
    }
    wasAlertedRef.current = false;
    wasTrimmedRef.current = false;
    if (!stringValue)  {
      setErrorFields?.(prev => ({...prev, [name]: false}));
      return;
    }
    const isDigitsOnly = DIGITS_ONLY_PATTERN.test(stringValue);
    if (!isDigitsOnly) {
      setErrorFields?.(prev => ({...prev, [name]: true}));
      return;
    }
    const numericValue = Number(stringValue);
    const isBelowMin = minValue !== undefined && numericValue < minValue;
    const isAboveMax = maxValue !== undefined && numericValue > maxValue;
    if (isBelowMin || isAboveMax) {
      AlertService.warning(`The value in "${label}" must be between ${minValue} and ${maxValue}.`);
      setErrorFields?.(prev => ({...prev, [name]: true}));
      return;
    }
    setErrorFields?.(prev => ({...prev, [name]: false}));
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (!wasFocusedRef.current) {
      // AlertService.warning(`Please enter only numbers in the ${label} field.`);
      wasFocusedRef.current = true;
    }
  };

  return (
    <StyledTextField
      {...rest}
      label={label}
      value={value}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleInputChange}
      error={error || errorFields}
      disabled={disabled || false}
      required={required || false}
      slotProps={{
        input: {
          endAdornment:
            isFocused ? (
              <InputAdornment position="end">
                <Typography
                  variant="caption"
                  color={errorFields ? 'error' : 'textSecondary'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {String(value).length}/{maxLength ? maxLength : MAX_LENGTH_FIELDS}
                </Typography>

              </InputAdornment>
            ) : null
        }
      }}
    />
  );
};